// LeadersDetailsPage.tsx
import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Container, Title, Card, Avatar, Badge, Button, Group, Stack, Text, Skeleton, Drawer,
  ActionIcon, SimpleGrid,
} from '@mantine/core';
import {
  IconArrowLeft, IconBriefcase2, IconCalendarClock,
  IconFileText, IconHeartHandshake, IconLayoutDashboard, IconMap, IconMapPin, IconPencil,
  IconRefresh,
} from '@tabler/icons-react';
import { useLeaderService, LeaderModelView } from '@actschurch/ccms-sdk';
import { resolveAvatarSrc, getInitialsFromName, reportAvatarLoadError } from '@/utils/avatar';
import Tabs from '@/components/ui/navigation/Tabs';
import { tabStyle } from '@/components/ui/TabStyle';
import { KpiCard } from '@/components/advanced/Widgets/kpi';

import OverviewTab from './Tabs/OverviewTab';
import ZonesTab from './Tabs/ZonesTab';
import LocationsTab from './Tabs/LocationsTab';
import NeedsTab from './Tabs/NeedsTab';
import CasesTab from './Tabs/CaseTab';
import AvailabilityTab from './Tabs/AvailabilityTab';
import DocumentsTab from './Tabs/DocumentTab';
import EditTab from './Tabs/EditTab';
import Flex from '@/components/ui/layout/Flex';
import Tooltip from '@/components/ui/overlays/Tooltip';
import { UnlistedAssigneeCard } from '../../Components/UnlistedAssigneeCard';

export default function LeadersDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const Leaders = useLeaderService();

  const [leader, setLeader] = useState<LeaderModelView | null>(null);
  const [activeTab, setActiveTab] = useState<string | null>('overview');
  const [loading, setLoading] = useState(true);

  /**
   * Name carried through from the list page for someone who owns a case but has no leader record. The
   * detail page cannot look it up, there is nothing to fetch, so the list passes what the case knew.
   */
  const unlistedName = (location.state as { unlistedName?: string } | null)?.unlistedName ?? null;

  // ── Edit drawer ──────────────────────────────────────────────────────────
  const [editOpen, setEditOpen] = useState(false);

  const fetchLeader = useCallback(async () => {
    if (!id) { setLoading(false); return; }
    setLoading(true);
    try {
      const result = await Leaders.getByUserId(id);
      setLeader(result);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [id, Leaders]);

  useEffect(() => { fetchLeader(); }, [fetchLeader]);

  const displayName = (() => {
    const fromParts = [leader?.firstName, leader?.surname].filter(Boolean).join(' ').trim();
    if (fromParts) return fromParts;
    const display = leader?.displayName?.trim();
    if (display && display !== leader?.userId) return display;
    return leader?.fullName?.trim() || 'Unknown';
  })();

  const displayAvatar = resolveAvatarSrc(leader?.avatar);
  const displayStatus = leader?.isActive ? 'Active' : 'Inactive';
  const displayProfessional = leader?.professional ? 'Professional' : '';

  // Counts backing the KPI cards below the profile card
  const zoneCount     = leader?.zones?.length     ?? 0;
  const locationCount = leader?.locations?.length ?? 0;
  const needCount      = leader?.needs?.length     ?? 0;

  if (!id) return null;

  /**
   * No leader record behind the id. Either the person was never given a user in Identity, or the record
   * was removed while a case still points at them. Previously this fell through to the normal layout and
   * rendered a silent empty shell, so it now says what is wrong and offers the fix.
   */
  if (!loading && !leader) {
    return (
      <Container size="xl" py="xl">
        <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={() => navigate(-1)}>
          Back
        </Button>
        <Title order={2} mb="md">Care Support Leader</Title>
        <UnlistedAssigneeCard
          userId={id}
          kind="leader"
          fallbackName={unlistedName}
          onCreated={fetchLeader}
        />
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
       <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={() => navigate(-1)}>
          Back
        </Button>
      {/* ── Header row: title + refresh (Edit now lives on the profile card) ── */}
      <Group justify="space-between" mb="sm">
        <Title order={2} >Care Support Leader</Title>

          <Group gap="sm">
             <Flex justify="right" align="center">
                <Tooltip label="Refresh">
                  <ActionIcon variant="subtle" onClick={fetchLeader} loading={loading} size="lg">
                    <IconRefresh size={16} />
                  </ActionIcon>
                </Tooltip>
              </Flex>
          </Group>
      </Group>

     


      {/* Profile card — avatar, name, badges, status, and bio */}
      <Card withBorder radius="md" padding="md" mb="md">
        {loading ? (
          <Group gap="md" align="center">
            <Skeleton circle height={56} />
            <Stack gap={8} style={{ flex: 1 }}>
              <Skeleton height={20} width="30%" />
              <Skeleton height={14} width="40%" />
            </Stack>
          </Group>
        ) : (
          <Group justify="space-between" align="flex-start">
            <Group gap="md" align="flex-start">
              {/* Initials are always the children so a broken image URL falls back to them
                  rather than Mantine's generic placeholder. */}
              <Avatar
                src={displayAvatar}
                alt={displayName}
                size={90}
                radius="md"
                imageProps={{ onError: () => reportAvatarLoadError(displayAvatar) }}
              >
                {getInitialsFromName(displayName)}
              </Avatar>
              <Stack gap={4}>
                <Title order={4}>{displayName}</Title>
                 <Text size="sm" c="dimmed" mt={4} maw={500}>
                  {leader?.bio || 'No bio provided'}
                </Text>
                <Group gap={6}>
                  <Badge
                    variant="light"
                    style={{
                      backgroundColor: leader?.typeColor ? `${leader.typeColor}22` : undefined,
                      color: leader?.typeColor ?? undefined,
                    }}
                  >
                    {leader?.typeName ?? 'Unknown Role'}
                  </Badge>
                  <Badge variant="outline" color="gray">
                    {leader?.capacityDisplay ?? ''}
                  </Badge>
                   {displayProfessional && (
                    <Badge variant="light">
                      {displayProfessional}
                    </Badge>
                  )}
                  <Badge color={displayStatus === 'Active' ? 'green' : 'red'} variant="filled">
                    {displayStatus}
                  </Badge>
                </Group>

              </Stack>
            </Group>
            <Button
              variant="filled"
              leftSection={<IconPencil size={16} />}
              onClick={() => setEditOpen(true)}
              disabled={!leader}
            >
              Edit
            </Button>
          </Group>
        )}
      </Card>

      
      {/* Tabs — Overview added, Edit removed (lives in the drawer above) */}
      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="overview" leftSection={<IconLayoutDashboard size={16} />} style={tabStyle(activeTab === 'overview')}>Overview</Tabs.Tab>
          <Tabs.Tab value="case" leftSection={<IconBriefcase2 size={16} />} style={tabStyle(activeTab === 'case')}>Cases</Tabs.Tab>
          <Tabs.Tab value="counseling" leftSection={<IconHeartHandshake size={16} />} style={tabStyle(activeTab === 'counseling')}>Needs</Tabs.Tab>
          <Tabs.Tab value="availability" leftSection={<IconCalendarClock size={16} />} style={tabStyle(activeTab === 'availability')}>Availability</Tabs.Tab>
          <Tabs.Tab value="locations" leftSection={<IconMapPin size={16} />} style={tabStyle(activeTab === 'locations')}>Locations</Tabs.Tab>
          <Tabs.Tab value="zones" leftSection={<IconMap size={16} />} style={tabStyle(activeTab === 'zones')}>Zones</Tabs.Tab>
          <Tabs.Tab value="documents" leftSection={<IconFileText size={16} />} style={tabStyle(activeTab === 'documents')}>Documents</Tabs.Tab>
        </Tabs.List>

        <Card withBorder radius="md" padding="lg" mt="sm">

          <Tabs.Panel value="overview" pt="md">
            <OverviewTab
              leader={leader}
              active={activeTab === 'overview'}
              zoneCount={zoneCount}
              locationCount={locationCount}
              needCount={needCount}
              onTabChange={setActiveTab} 
              onRemoved={() => navigate(-1)}
            />
          </Tabs.Panel>

          <Tabs.Panel value="case" pt="md">
            <CasesTab leaderId={id} active={activeTab === 'case'} />
          </Tabs.Panel>

          <Tabs.Panel value="zones" pt="md">
            <ZonesTab leaderId={id} active={activeTab === 'zones'} onLeaderChange={fetchLeader} />
          </Tabs.Panel>

          <Tabs.Panel value="locations" pt="md">
            <LocationsTab leaderId={id} active={activeTab === 'locations'} onLeaderChange={fetchLeader} />
          </Tabs.Panel>

          <Tabs.Panel value="counseling" pt="md">
            <NeedsTab leaderId={id} active={activeTab === 'counseling'} onLeaderChange={fetchLeader} />
          </Tabs.Panel>

          <Tabs.Panel value="availability" pt="md">
            <AvailabilityTab leaderId={id} active={activeTab === 'availability'} onLeaderChange={fetchLeader} />
          </Tabs.Panel>

          <Tabs.Panel value="documents" pt="md">
            <DocumentsTab leaderId={id} active={activeTab === 'documents'} onLeaderChange={fetchLeader} />
          </Tabs.Panel>
        </Card>
      </Tabs>

      {/* ── Edit Drawer ── */}
      <Drawer
        opened={editOpen}
        onClose={() => setEditOpen(false)}
        title={
          <Group gap="xs">
            <IconPencil size={18} />
            <Text fw={600}>Edit Leader</Text>
          </Group>
        }
        position="right"
        size="md"
        padding="md"
      >
        <EditTab
          leader={leader}
          onSaved={() => { fetchLeader(); setEditOpen(false); }}
        />
      </Drawer>
    </Container>
  );
}